import { Layouts } from 'react-grid-layout';
import _ from 'lodash';

import { LSHelper } from '@crm-framework/utils';

import {
  IGetLayouts,
  ILayoutMinSizes,
  IResetLayouts,
  ISaveLayouts,
} from './models';

export function checkIsOpenLayouts(
  localLayouts: Layouts,
  panelStorageName: string
): Layouts {
  const panelModes = LSHelper.getObject(panelStorageName);
  let newLayout = { ...localLayouts };

  if (panelModes) {
    const parsedModeKeys = Object.keys(panelModes);
    newLayout = Object.keys(localLayouts).reduce(
      (layoutObject, size) => ({
        ...layoutObject,
        [size]: localLayouts[size].map((item) => {
          if (parsedModeKeys.includes(item.i) && !panelModes[item.i]) {
            return { ...item, h: 2 };
          }

          return item;
        }),
      }),
      {}
    );
  }

  return newLayout;
}

export function getLayoutsWithMinSizes(
  layout: Layouts,
  layoutsMinSizes?: ILayoutMinSizes
): Layouts {
  const layoutKeys = Object.keys(layout);
  const layoutsWithMinSizes = layoutKeys.reduce((newLayouts, sizeKey) => {
    const newlayout = layout[sizeKey].map((panel) => {
      let minSizes = { i: panel.i, minW: 2, minH: 5 };

      if (layoutsMinSizes) {
        minSizes = layoutsMinSizes[sizeKey].find(
          (panelMinSizes) => panelMinSizes.i === panel.i
        )!;
      }

      return { ...panel, ...minSizes };
    });

    return { ...newLayouts, [sizeKey]: newlayout };
  }, {});

  return layoutsWithMinSizes;
}

export function filterLayoutsByVisiblePanels({
  layout,
  visiblePanelsList,
}: {
  layout: Layouts;
  visiblePanelsList?: string[];
}): Layouts {
  const layoutKeys = Object.keys(layout);

  if (visiblePanelsList)
    return layoutKeys.reduce(
      (filteredLayout, key) => ({
        ...filteredLayout,
        [key]: layout[key].reduce(
          (panelList, panel) =>
            visiblePanelsList.includes(panel.i)
              ? [...panelList, panel]
              : panelList,
          []
        ),
      }),
      {}
    );

  return layout;
}

export function getVisiblePanelList({
  layout,
  visiblePanelsList,
  withPanelsMenu,
}: {
  layout: Layouts;
  withPanelsMenu: boolean;
  visiblePanelsList?: string[];
}): Layouts {
  return withPanelsMenu
    ? filterLayoutsByVisiblePanels({
        layout,
        visiblePanelsList,
      })
    : layout;
}

export function getLayouts({
  save = false,
  storageName,
  defaultLayout,
  layoutsMinSizes,
  panelStorageName,
}: IGetLayouts): Layouts {
  let localLayouts = LSHelper.getObject<Layouts>(storageName);

  if (!localLayouts) {
    localLayouts = defaultLayout;
  } else {
    localLayouts = Object.entries(localLayouts).reduce<Layouts>(
      (acc, [key, value]) => {
        if (value.length !== defaultLayout[key].length) {
          acc[key] = _.unionBy(defaultLayout[key], value, 'i');
        }

        return acc;
      },
      { ...localLayouts }
    );
  }

  const currentLayouts: Layouts = checkIsOpenLayouts(
    localLayouts,
    panelStorageName
  );

  if (save) {
    saveLayouts({ newLayout: localLayouts, storageName });
  }

  return getLayoutsWithMinSizes(currentLayouts, layoutsMinSizes);
}

export function saveLayouts({ newLayout, storageName }: ISaveLayouts) {
  const filteredNewLayout = Object.keys(newLayout).reduce(
    (filteredLayout, key) => ({
      ...filteredLayout,
      [key]: newLayout[key].map((item) => {
        delete item.minH;
        delete item.minW;

        return item;
      }),
    }),
    {}
  );
  LSHelper.setItem(storageName, filteredNewLayout);
}

export function resetLayouts({
  storageName,
  defaultLayout,
  layoutsMinSizes,
  panelStorageName,
}: IResetLayouts): Layouts {
  LSHelper.removeItem(storageName);
  LSHelper.removeItem(panelStorageName);

  return getLayouts({
    save: true,
    storageName,
    defaultLayout,
    layoutsMinSizes,
    panelStorageName,
  });
}

type CheckLocalStorageType = {
  isCollapsible: boolean;
  panelStorageName: string;
  panelKey: string;
  defaultIsOpen?: boolean;
};

export const getIsOpenFromLocalStorage = ({
  isCollapsible,
  panelStorageName,
  panelKey,
  defaultIsOpen,
}: CheckLocalStorageType): boolean => {
  if (defaultIsOpen) return defaultIsOpen;

  if (isCollapsible) {
    const currentMode = localStorage.getItem(panelStorageName);
    const parsedMode = currentMode ? JSON.parse(currentMode) : {};

    if (panelKey && Object.keys(parsedMode).includes(panelKey)) {
      return parsedMode[panelKey];
    }
  }

  return true;
};
